import React from 'react';
import useHasError from '../hooks/useHasError';
import useHasErrorInAreas from '../hooks/useHasErrorInAreas';
import useIsSyncSettled from '../hooks/useIsSyncSettled';
import useProperty from '../hooks/useProperty';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { useDocumentStore } from '../hooks/useDocumentStore';
import { useDocumentActions } from '../hooks/useDocumentActions';
import ConfirmEditorialSendToPublishDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishDialog';
import ConfirmEditorialSendToPublishProcessCertificationDialog from 'components/v2/dialogs/ConfirmEditorialSendToPublishProcessCertificationDialog';
import DeclinePublicationDialog from 'components/v2/dialogs/DeclinePublicationDialog';
import DeclineVerificationDialog from 'components/v2/dialogs/DeclineVerificationDialog';
import DepublishAdminDialog from 'components/v2/dialogs/DepublishAdminDialog';
import LicenseeAdminReviewDialog from 'components/v2/dialogs/LicenseeAdminReviewDialog';
import UpdateEpdTypeSelectionDialog from 'components/v2/dialogs/UpdateEpdTypeSelectionDialog';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import {
  postApproveDocumentVerification,
  postRetractDocumentVerification,
  postPublishDocument,
  postCancelDocumentDeregistration,
  postSendDocumentForPublication,
  postStartDocumentVerification,
} from 'services/api/dynamicFormsApi';
import { DocumentActionState } from 'types/dynamic-forms/DocumentModelStateActions';
import { DocumentPropertyFieldsAreaId } from 'types/dynamic-forms/DocumentPropertyFieldsAreaId';
import { ButtonsList } from './DocumentButtons.styles';
import { VerificationType } from 'services/EpdClient';

const DocumentButtons: React.FC = () => {
  const { docId, state, metadata } = useDocumentStore();
  const hasErrors = useHasError([]);
  // Start Verification depends on steps 1-4 only: validate the document-input and verification areas,
  // but ignore the step-5 "confirmation" (service-agreement) area, which is reset on every update and
  // gated separately by the publish actions. Mirrors the backend DocumentStateManager gate.
  const hasErrorsForVerification = useHasErrorInAreas([
    DocumentPropertyFieldsAreaId.Main,
    DocumentPropertyFieldsAreaId.VerificationAssignment,
    DocumentPropertyFieldsAreaId.VerifierSelection,
    DocumentPropertyFieldsAreaId.PreVerificationTool,
    DocumentPropertyFieldsAreaId.Verification,
  ]);
  // Keep submit/transition actions disabled until the optimistic edits are actually persisted,
  // so a failed/pending save can't leave a button enabled with unsaved data.
  const isSyncSettled = useIsSyncSettled();
  const hasUnsavedOrInvalidChanges = hasErrors || !isSyncSettled;
  const hasUnsavedOrInvalidChangesForVerification = hasErrorsForVerification || !isSyncSettled;
  const serviceAgreement = useProperty<boolean>(['serviceAgreementStatus']);
  const agreementConsent = serviceAgreement.value ?? false;
  const verificationType = useProperty<string>(['verificationType']);
  const isProcessCertification = verificationType.value === VerificationType.EPDProcessCertification;
  // Passed to onStartUpdate, which owns the branch: only an EPD Verification document is asked which
  // kind of update it is. Process Certification, pre-verified tool and a not-yet-loaded verification
  // type all start an update directly.
  const isEpdVerification = verificationType.value === VerificationType.EPDVerification;

  const {
    doAction,
    showSendForPublishSuccessToast,
    isSpinnerVisible,
    openDialog,
    setOpenDialog,
    onDeclinePublicationConfirmed,
    onDeclineVerificationConfirmed,
    onCancelVerification,
    onCancelUpdate,
    onCancelPublication,
    onDeregister,
    onEditorialSendToPublishConfirmed,
    updateType,
    setUpdateType,
    onStartUpdate,
    onStartUpdateConfirmed,
    onLicenseeAdminReviewConfirmed,
    onDepublishByAdminConfirmed,
    onDepublishByCustomer,
    onDeleteDraft,
  } = useDocumentActions();

  const a = state.actions;

  return (
    <ButtonsList>
      {isSpinnerVisible && <OverlaySpinner />}

      {a.deleteDraft !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Delete EPD"
          onClick={onDeleteDraft}
          disabled={a.deleteDraft === DocumentActionState.Disabled}
        />
      )}
      {a.cancelUpdate !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Cancel update"
          onClick={onCancelUpdate}
          disabled={a.cancelUpdate === DocumentActionState.Disabled}
        />
      )}
      {a.cancelVerification !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Cancel verification"
          onClick={onCancelVerification}
          disabled={a.cancelVerification === DocumentActionState.Disabled}
        />
      )}
      {a.declineVerification !== DocumentActionState.Hidden && (
        <>
          <SecondaryButton
            label="Decline verification"
            onClick={() => setOpenDialog('declineVerification')}
            disabled={a.declineVerification === DocumentActionState.Disabled}
          />
          <DeclineVerificationDialog
            isOpened={openDialog === 'declineVerification'}
            epd={metadata}
            onClose={onDeclineVerificationConfirmed}
          />
        </>
      )}
      {a.retractVerification !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Retract verification"
          onClick={() => doAction(() => postRetractDocumentVerification(docId))}
          disabled={a.retractVerification === DocumentActionState.Disabled}
        />
      )}
      {a.cancelPublication !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Cancel publication"
          onClick={onCancelPublication}
          disabled={a.cancelPublication === DocumentActionState.Disabled}
        />
      )}
      {a.declinePublication !== DocumentActionState.Hidden && (
        <>
          <SecondaryButton
            label="Decline publication"
            onClick={() => setOpenDialog('declinePublication')}
            disabled={a.declinePublication === DocumentActionState.Disabled}
          />
          <DeclinePublicationDialog
            isOpened={openDialog === 'declinePublication'}
            epd={metadata}
            onClose={(payload) => {
              if (payload) {
                onDeclinePublicationConfirmed(payload);
              } else {
                setOpenDialog(null);
              }
            }}
          />
        </>
      )}
      {a.startUpdate !== DocumentActionState.Hidden && (
        <>
          <PrimaryButton
            label="Update EPD"
            onClick={() => onStartUpdate(isEpdVerification)}
            disabled={a.startUpdate === DocumentActionState.Disabled}
          />
          <UpdateEpdTypeSelectionDialog
            visible={openDialog === 'startUpdate'}
            updateType={updateType}
            onUpdateTypeChange={setUpdateType}
            onAccept={onStartUpdateConfirmed}
            onHide={() => setOpenDialog(null)}
          />
        </>
      )}
      {a.startVerification !== DocumentActionState.Hidden && (
        <PrimaryButton
          label="Start verification"
          onClick={() => doAction(() => postStartDocumentVerification(docId))}
          disabled={a.startVerification === DocumentActionState.Disabled || hasUnsavedOrInvalidChangesForVerification}
        />
      )}
      {a.approveVerification !== DocumentActionState.Hidden && (
        <PrimaryButton
          label="Approve"
          onClick={() => doAction(() => postApproveDocumentVerification(docId))}
          disabled={a.approveVerification === DocumentActionState.Disabled || hasUnsavedOrInvalidChanges}
        />
      )}
      {a.sendForPublication !== DocumentActionState.Hidden && (
        <PrimaryButton
          label="Send for publication"
          onClick={() => doAction(() => postSendDocumentForPublication(docId), true, showSendForPublishSuccessToast)}
          disabled={a.sendForPublication === DocumentActionState.Disabled || !agreementConsent || hasUnsavedOrInvalidChanges}
        />
      )}
      {a.sendForPublicationEditorial !== DocumentActionState.Hidden && (
        <>
          <PrimaryButton
            label="Send to publish"
            onClick={() => setOpenDialog('editorialSendToPublish')}
            disabled={a.sendForPublicationEditorial === DocumentActionState.Disabled || !agreementConsent || hasUnsavedOrInvalidChanges}
          />
          {isProcessCertification ? (
            <ConfirmEditorialSendToPublishProcessCertificationDialog
              isOpened={openDialog === 'editorialSendToPublish'}
              onClose={onEditorialSendToPublishConfirmed}
            />
          ) : (
            <ConfirmEditorialSendToPublishDialog
              isOpened={openDialog === 'editorialSendToPublish'}
              onClose={onEditorialSendToPublishConfirmed}
            />
          )}
        </>
      )}
      {a.licenseeAdminSendToPublish !== DocumentActionState.Hidden && (
        <>
          <PrimaryButton
            label="Send to publish"
            onClick={() => setOpenDialog('licenseeAdminReview')}
            disabled={a.licenseeAdminSendToPublish === DocumentActionState.Disabled || hasUnsavedOrInvalidChanges}
          />
          <LicenseeAdminReviewDialog
            isOpened={openDialog === 'licenseeAdminReview'}
            onClose={onLicenseeAdminReviewConfirmed}
          />
        </>
      )}
      {a.publish !== DocumentActionState.Hidden && (
        <PrimaryButton
          label="Publish"
          onClick={() => doAction(() => postPublishDocument(docId))}
          disabled={a.publish === DocumentActionState.Disabled || hasUnsavedOrInvalidChanges}
        />
      )}
      {a.depublishByAdmin !== DocumentActionState.Hidden && (
        <>
          <SecondaryButton
            label="Depublish"
            onClick={() => setOpenDialog('depublishAdmin')}
            disabled={a.depublishByAdmin === DocumentActionState.Disabled}
          />
          <DepublishAdminDialog
            isOpened={openDialog === 'depublishAdmin'}
            epd={metadata ? { epdNumber: metadata.fullIdentificationNumber, title: metadata.title } : null}
            onClose={onDepublishByAdminConfirmed}
          />
        </>
      )}
      {a.depublishByCustomer !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Depublish"
          onClick={onDepublishByCustomer}
          disabled={a.depublishByCustomer === DocumentActionState.Disabled}
        />
      )}
      {a.deregister !== DocumentActionState.Hidden && (
        <SecondaryButton
          label="Deregister EPD"
          onClick={onDeregister}
          disabled={a.deregister === DocumentActionState.Disabled}
        />
      )}
      {a.cancelDeregistration !== DocumentActionState.Hidden && (
        <PrimaryButton
          label="Cancel deregistration"
          onClick={() => doAction(() => postCancelDocumentDeregistration(docId))}
          disabled={a.cancelDeregistration === DocumentActionState.Disabled}
        />
      )}
    </ButtonsList>
  );
};

export default DocumentButtons;
